import { apiClient } from './client';

// Mirrors packages/api/src/modules/gtm-plays/ (the web app keeps its own copy,
// as sourcing.ts and signalOps.ts do).

export type PlayId =
  | 'signal_01_bid_desk_hiring'
  | 'signal_02_won_public_tender'
  | 'signal_03_bid_and_lost'
  | 'signal_04a_re_tender_incumbent'
  | 'signal_04b_re_tender_challenger'
  | 'signal_05_new_bid_leader'
  | 'signal_06_read_our_material'
  | 'signal_07_ai_for_bids_conversation';

export type CandidateStatus = 'proposed' | 'confirmed' | 'enrolled' | 'blocked' | 'dismissed';

export const CANDIDATE_STATUS_LABELS: Record<CandidateStatus, string> = {
  proposed: 'Proposed',
  confirmed: 'Confirming…',
  enrolled: 'Enrolled',
  blocked: 'Blocked',
  dismissed: 'Dismissed',
};

export const PERSON_SOURCE_LABELS: Record<string, string> = {
  contacts_tab: 'Contacts tab',
  attio: 'Attio',
  rb2b: 'RB2B',
  jungler: 'Jungler',
  apollo: 'Apollo',
};

/** Where the candidate's company domain came from (evaluate.ts `domainSource`). */
export const DOMAIN_SOURCE_LABELS: Record<string, string> = {
  sourcing: 'from the source',
  companies_tab: 'Companies tab',
  tracked_company: 'tracked company',
  earlier_candidate: 'earlier run',
  apollo: 'Apollo',
};

/** The candidate evidence fields the queue reads (the rest is source-specific). */
export interface CandidateEvidence {
  copyVariables?: Record<string, string>;
  missingCopyVariables?: string[];
  apolloLeads?: Array<{ name: string; title: string | null }>;
  domainSource?: string | null;
  sourceType?: string;
  alsoSourcedAs?: string[];
  /** Where the person sits (null = the source gave no location). Absent on candidates evaluated before 2026-10-09. */
  personCountry?: string | null;
  /** The company's own country from the sourcing data. Absent on candidates evaluated before 2026-10-09. */
  companyCountry?: string | null;
  [key: string]: unknown;
}

export interface PlayOverviewRow {
  playId: PlayId;
  label: string;
  rationale: string;
  campaign: { id: string; name: string; status: string | null } | null;
  ambiguous: string[];
  leadsInCampaign: number;
  detectorGap: string | null;
  /** Per status; from 13 Oct 2026 the API leaves persona false positives ("Bar Tender") out, as the queue does. */
  counts: Record<CandidateStatus, number>;
  /** Candidates per status the queue leaves out because the title is not a bid-team role. Older API builds omit it. */
  personaHidden?: Record<CandidateStatus, number>;
}

export interface PlayCandidate {
  id: string;
  playId: PlayId;
  companyName: string;
  companyDomain: string | null;
  country: string | null;
  personName: string | null;
  personTitle: string | null;
  personEmail: string | null;
  personLinkedinUrl: string | null;
  personSource: string | null;
  needsContact: boolean;
  evidenceSummary: string;
  evidence: CandidateEvidence | null;
  status: CandidateStatus;
  blockedReason: string | null;
  lemlistCampaignId: string | null;
  lemlistCampaignName: string | null;
  lemlistLeadId: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  enrolledAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  /**
   * True when the person's title is a known false positive for the bid-team
   * persona ("Bar Tender"); the API's PERSONA_EXCLUDED_TITLE_PATTERNS decide.
   * The queue hides these rows and says how many. Older API builds omit it.
   */
  personaExcluded?: boolean;
}

export interface ApolloReport {
  /** Whether this run was allowed to call Apollo (it costs credits, so it is opt-in). */
  requested: boolean;
  /** Companies that needed a lookup but Apollo was not requested. */
  notRequested: number;
  /** Companies skipped because Apollo was asked about them in the last 30 days. */
  recentlyLookedUp: number;
  lookedUp: number;
  found: number;
  withLinkedin: number;
  capped: number;
  errors: string[];
}

export interface DetectorReport {
  playId: PlayId;
  scanned: number;
  proposed: number;
  withoutContact: number;
  skipped: number;
  skippedWhy: string[];
  notRun: string | null;
  apollo?: ApolloReport;
}

export interface EvaluateResult {
  reports: DetectorReport[];
  contactsTabRows: number;
  warnings: string[];
}

export interface ConfirmResult {
  candidate: PlayCandidate;
  outcome: 'enrolled' | 'blocked';
  reason: string | null;
}

export function getPlaysOverview(): Promise<{ plays: PlayOverviewRow[]; lastEvaluatedAt?: string | null }> {
  return apiClient.get('/gtm-plays/overview');
}

/** Rows one selected play loads (the API allows up to 2,000); still bounded, and "top N of M" past it. */
export const PLAY_CANDIDATE_LIMIT_ONE_PLAY = 1000;

export function getPlayCandidates(params: { playId?: PlayId; statuses?: CandidateStatus[] }): Promise<{
  count: number;
  /** Candidates matching before the list cap; older API builds omit it. */
  total?: number;
  limit?: number;
  rows: PlayCandidate[];
}> {
  const qs = new URLSearchParams();
  if (params.playId) qs.set('playId', params.playId);
  if (params.statuses && params.statuses.length > 0) qs.set('statuses', params.statuses.join(','));
  // One play reads up to PLAY_CANDIDATE_LIMIT_ONE_PLAY, so 04A's 696 fit; "All plays" keeps the server's 500.
  if (params.playId) qs.set('limit', String(PLAY_CANDIDATE_LIMIT_ONE_PLAY));
  const q = qs.toString();
  return apiClient.get(`/gtm-plays/candidates${q ? `?${q}` : ''}`);
}

/** Company blocks per page of the queue (the API's CANDIDATE_PAGE_COMPANIES, the kit Pagination's 15). */
export const PLAY_CANDIDATE_PAGE_SIZE = 15;

export interface PlayCandidatePage {
  count: number;
  /** Candidate rows matching across every page. */
  total: number;
  /** Company blocks (play + company name) matching across every page; the pager counts these. */
  companies: number;
  /**
   * Candidates matching but left out because the title is not a bid-team role.
   * When present, the server already dropped them and folded the companies
   * before paging; older API builds omit it and flag rows with `personaExcluded`.
   */
  personaHidden?: number;
  page: number;
  pageSize: number;
  rows: PlayCandidate[];
}

/**
 * One page of the queue, paged by company on the server, so a play of any size
 * is reachable (04A held 1,618 proposed rows on 12 Oct 2026; one read of all
 * of them was 4.7 MB) and each read stays small.
 */
export function getPlayCandidatePage(params: {
  playId?: PlayId;
  statuses?: CandidateStatus[];
  page: number;
}): Promise<PlayCandidatePage> {
  const qs = new URLSearchParams();
  if (params.playId) qs.set('playId', params.playId);
  if (params.statuses && params.statuses.length > 0) qs.set('statuses', params.statuses.join(','));
  qs.set('page', String(params.page));
  return apiClient.get(`/gtm-plays/candidates?${qs.toString()}`);
}

/** Max companies one Apollo-enabled evaluation may look up (the API's APOLLO_PEOPLE_LOOKUPS_PER_RUN). */
export const APOLLO_LOOKUPS_PER_RUN = 40;

/** `apollo: true` opts in to the Apollo domain + people lookup, which consumes Apollo credits. */
export function evaluatePlays(options: { apollo?: boolean } = {}): Promise<EvaluateResult> {
  return apiClient.post('/gtm-plays/evaluate', options.apollo ? { apollo: true } : {});
}

export function confirmCandidate(id: string): Promise<ConfirmResult> {
  return apiClient.post(`/gtm-plays/candidates/${id}/confirm`, {});
}

export function dismissCandidate(id: string, note?: string): Promise<{ candidate: PlayCandidate }> {
  return apiClient.post(`/gtm-plays/candidates/${id}/dismiss`, note ? { note } : {});
}
